import React, { useState, useCallback } from 'react';
import { GAME_MODES } from '../utils/phaseDefinitions';
import { getCPUAvatar, getCPUGradient } from '../utils/avatarHelpers';

// Random name generator for players who don't enter a name
const PLAYER_ADJECTIVES = [
  'Swift', 'Lucky', 'Bold', 'Brave', 'Cool', 'Epic', 'Grand', 'Happy',
  'Jolly', 'Keen', 'Mighty', 'Noble', 'Quick', 'Royal', 'Super', 'Wild'
];

const PLAYER_NOUNS = [
  'Ace', 'Bear', 'Card', 'Duke', 'Eagle', 'Fox', 'Gem', 'Hawk',
  'King', 'Lion', 'Owl', 'Panda', 'Queen', 'Star', 'Tiger', 'Wolf'
];

const generateRandomName = () => {
  const adj = PLAYER_ADJECTIVES[Math.floor(Math.random() * PLAYER_ADJECTIVES.length)];
  const noun = PLAYER_NOUNS[Math.floor(Math.random() * PLAYER_NOUNS.length)];
  return `${adj}${noun}`;
};

// Game type options (no Choice mode, no emoji icons)
const GAME_TYPES = [
  { value: 'normal', label: 'Normal', description: 'Complete phases in order' },
  { value: 'chaos',  label: 'Chaos',  description: 'Random phase each round' }
];

// Player color palette for avatar rings
const PLAYER_COLORS = ['#d4a843','#c0392b','#27ae60','#3498db','#9b59b6','#e67e22'];

function Lobby({
  connected,
  playerId,
  roomCode,
  room,
  onCreateRoom,
  onJoinRoom,
  onAddCPU,
  onRemoveCPU,
  onUpdateSettings,
  onLeaveRoom,
  onStartGame,
  preloadProgress = 0,
  preloadComplete = false
}) {
  const [playerName, setPlayerName] = useState('');
  const [joinCode,   setJoinCode]   = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedGameType, setSelectedGameType] = useState('normal');
  const [settings, setSettings] = useState({ maxPlayers: 4, mode: 'normal10' });

  const updateGameMode = (gameType) => {
    const newMode = `${gameType}10`;
    setSettings(prev => ({ ...prev, mode: newMode }));
  };

  const isHost   = room && room.hostId === playerId;
  const canStart = room && room.players.length >= 2;

  const getPlayerName = useCallback(() => {
    return playerName.trim() || generateRandomName();
  }, [playerName]);

  const handleCreate = useCallback(() => {
    const name = getPlayerName();
    setPlayerName(name);
    onCreateRoom(name, settings);
  }, [getPlayerName, onCreateRoom, settings]);

  const handleJoin = useCallback(() => {
    const name = getPlayerName();
    setPlayerName(name);
    onJoinRoom(name, joinCode);
  }, [getPlayerName, onJoinRoom, joinCode]);

  /* ============================================================
     LOGIN SCREEN  (before entering a room)
     ============================================================ */
  if (!roomCode) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-end pb-6 p-4 bg-cover bg-center bg-no-repeat relative"
        style={{ backgroundImage: "url('/assets/login_bg.png')" }}
      >
        {/* Dark gradient overlay - Removed/Reduced to allow original image brightness */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent z-0" />

        {/* Panel */}
        <div
          className="w-full max-w-md space-y-5 relative z-10 rounded-lg p-6"
          style={{
            background: 'linear-gradient(160deg, rgba(13,41,24,0.92) 0%, rgba(13,31,20,0.95) 100%)',
            border: '1px solid rgba(212,168,67,0.35)',
            boxShadow: '0 0 0 1px rgba(13,31,20,0.9), 0 20px 60px rgba(0,0,0,0.6), 0 0 20px rgba(212,168,67,0.08)',
            backdropFilter: 'blur(6px)',
          }}
        >
          {/* Divider with Chinese ornament */}
          <div className="flex items-center gap-3 pb-1">
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.4))' }} />
            <span style={{ color: 'rgba(212,168,67,0.7)', fontSize: '0.7rem', letterSpacing: '0.2em', fontFamily: '"Cinzel", serif' }}>ENTER</span>
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(212,168,67,0.4), transparent)' }} />
          </div>

          {/* Player name input */}
          <div>
            <label className="block text-xs mb-2" style={{ color: 'rgba(212,168,67,0.7)', fontFamily: '"Cinzel", serif', letterSpacing: '0.1em' }}>
              YOUR NAME
            </label>
            <input
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder="Enter name (or leave blank for random)"
              className="input-field"
              maxLength={20}
            />
          </div>

          {!showCreate ? (
            <>
              {/* Create room button */}
              <button
                onClick={() => setShowCreate(true)}
                disabled={!connected}
                className="btn-primary w-full py-4 text-base"
                style={{ fontSize: '1rem', letterSpacing: '0.12em' }}
              >
                ✦ Create New Game ✦
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px" style={{ background: 'rgba(212,168,67,0.2)' }} />
                <span style={{ color: 'rgba(245,234,210,0.4)', fontSize: '0.75rem', fontFamily: '"Noto Serif SC", serif' }}>or join existing</span>
                <div className="flex-1 h-px" style={{ background: 'rgba(212,168,67,0.2)' }} />
              </div>

              {/* Join room */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="Room Code"
                  className="input-field flex-1"
                  maxLength={6}
                  style={{ fontFamily: '"Cinzel", serif', letterSpacing: '0.2em', textAlign: 'center' }}
                />
                <button
                  onClick={handleJoin}
                  disabled={!connected || joinCode.length !== 6}
                  className="btn-secondary"
                  style={{ minWidth: '80px' }}
                >
                  Join
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Game settings panel - compact for mobile */}
              <div className="space-y-3" style={{
                background: 'rgba(13,31,20,0.5)',
                border: '1px solid rgba(212,168,67,0.15)',
                borderRadius: '6px',
                padding: '14px',
              }}>
                <h3 style={{ fontFamily: '"Cinzel",serif', color: '#d4a843', letterSpacing: '0.12em', fontSize: '0.85rem', marginBottom: '4px' }}>
                  GAME SETTINGS
                </h3>

                {/* Max Players */}
                <div>
                  <label className="block text-xs mb-1.5" style={{ color: 'rgba(212,168,67,0.6)', fontFamily: '"Cinzel",serif', letterSpacing: '0.1em' }}>
                    MAX PLAYERS
                  </label>
                  <select
                    value={settings.maxPlayers}
                    onChange={(e) => setSettings({ ...settings, maxPlayers: parseInt(e.target.value) })}
                    className="select-field"
                  >
                    {[2, 3, 4].map(n => (
                      <option key={n} value={n}>{n} Players</option>
                    ))}
                  </select>
                </div>

                {/* Game Type — 2 cols, no icons */}
                <div>
                  <label className="block text-xs mb-1.5" style={{ color: 'rgba(212,168,67,0.6)', fontFamily: '"Cinzel",serif', letterSpacing: '0.1em' }}>
                    GAME TYPE
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {GAME_TYPES.map(type => (
                      <button
                        key={type.value}
                        onClick={() => { setSelectedGameType(type.value); updateGameMode(type.value); }}
                        style={{
                          padding: '10px 8px',
                          borderRadius: '5px',
                          border: selectedGameType === type.value
                            ? '1px solid rgba(212,168,67,0.8)'
                            : '1px solid rgba(212,168,67,0.2)',
                          background: selectedGameType === type.value
                            ? 'rgba(212,168,67,0.15)'
                            : 'rgba(13,31,20,0.6)',
                          transition: 'all 0.2s ease',
                          cursor: 'pointer',
                          textAlign: 'center',
                          boxShadow: selectedGameType === type.value ? '0 0 10px rgba(212,168,67,0.2)' : 'none',
                        }}
                      >
                        <div style={{ fontFamily: '"Cinzel",serif', fontWeight: '700', fontSize: '0.85rem', color: selectedGameType === type.value ? '#d4a843' : '#f5ead2' }}>
                          {type.label}
                        </div>
                        <div style={{ fontSize: '0.62rem', color: 'rgba(245,234,210,0.5)', marginTop: '2px' }}>
                          {type.description}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Selected Mode Summary - compact */}
                <div style={{
                  background: 'rgba(212,168,67,0.07)',
                  border: '1px solid rgba(212,168,67,0.2)',
                  borderRadius: '5px',
                  padding: '8px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}>
                  <div style={{ fontSize: '0.65rem', color: 'rgba(212,168,67,0.55)', fontFamily: '"Cinzel",serif', letterSpacing: '0.06em', flexShrink: 0 }}>
                    MODE
                  </div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#d4a843', fontFamily: '"Cinzel",serif' }}>
                    {GAME_MODES[settings.mode]?.name || 'Normal Phase 10'}
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setShowCreate(false)} className="btn-secondary flex-1">
                  ← Back
                </button>
                <button onClick={handleCreate} disabled={!connected} className="btn-primary flex-1">
                  Create Room →
                </button>
              </div>
            </>
          )}
        </div>

        {/* Connection status */}
        <div className="mt-5 relative z-10 text-xs" style={{ color: 'rgba(245,234,210,0.4)' }}>
          {connected ? (
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full animate-lantern-pulse" style={{ background: '#27ae60', boxShadow: '0 0 6px #27ae60' }} />
              Connected
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#d4a843' }} />
              Connecting...
            </span>
          )}
        </div>
      </div>
    );
  }

  /* ============================================================
     WAITING ROOM
     ============================================================ */
  return (
    <div
      className="min-h-screen flex flex-col items-center p-4 py-3 bg-cover bg-center bg-no-repeat relative"
      style={{ backgroundImage: "url('/assets/lobby_bg.png')" }}
    >
      {/* Warm dark overlay */}
      <div className="absolute inset-0 z-0" style={{ background: 'rgba(10,6,2,0.65)', backdropFilter: 'blur(1px)' }} />

      <div className="relative z-10 w-full flex flex-col items-center max-w-lg">
        {/* Header — compact */}
        <div className="text-center mb-3">
          <div className="flex items-center gap-3 mb-2">
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,168,67,0.6))' }} />
            <span style={{ color: 'rgba(212,168,67,0.6)', fontSize: '0.6rem' }}>◆◆◆</span>
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, rgba(212,168,67,0.6), transparent)' }} />
          </div>
          <h1 style={{
            fontFamily: '"Cinzel", serif',
            fontSize: '1.5rem',
            fontWeight: '900',
            letterSpacing: '0.15em',
            color: '#d4a843',
            textShadow: '0 0 20px rgba(212,168,67,0.5), 0 2px 4px rgba(0,0,0,0.8)',
            lineHeight: 1.1,
          }}>
            WAITING ROOM
          </h1>
          {/* 「等候室」副标题已移除 */}
        </div>

        {/* Room code */}
        <div className="text-center mb-2">
          <p style={{ fontSize: '0.6rem', color: 'rgba(245,234,210,0.5)', fontFamily: '"Cinzel",serif', letterSpacing: '0.12em', marginBottom: '6px' }}>
            SHARE THIS CODE
          </p>
          <div className="room-code select-all cursor-pointer" title="Click to copy" style={{ fontSize: '1.4rem', padding: '8px 20px', letterSpacing: '0.25em' }}>
            {roomCode}
          </div>
        </div>

        {/* Game mode info */}
        <div className="text-center mb-3" style={{
          background: 'rgba(13,31,20,0.7)',
          border: '1px solid rgba(212,168,67,0.2)',
          borderRadius: '5px',
          padding: '6px 20px',
        }}>
          <span style={{ color: 'rgba(245,234,210,0.4)', fontSize: '0.72rem' }}>Mode: </span>
          <span style={{ color: '#d4a843', fontWeight: '700', fontFamily: '"Cinzel",serif', fontSize: '0.85rem' }}>
            {GAME_MODES[room?.settings?.mode]?.name || 'Normal Phase 10'}
          </span>
        </div>


        {/* Players list — compact */}
        <div className="w-full mb-2">
          <div className="flex items-center justify-between mb-2">
            <h3 style={{ fontFamily: '"Cinzel",serif', color: '#d4a843', fontSize: '0.8rem', letterSpacing: '0.12em' }}>
              PLAYERS
            </h3>
            <span style={{ color: 'rgba(245,234,210,0.5)', fontSize: '0.75rem' }}>
              {room?.players?.length || 0} / {room?.settings?.maxPlayers || 6}
            </span>
          </div>

          <div className="space-y-1">
            {room?.players?.map((player, idx) => (
              <div
                key={player.id}
                className={`player-item ${player.id === room.hostId ? 'is-host' : ''} ${player.isComputer ? 'is-cpu' : ''}`}
                style={{ padding: '6px 12px' }}
              >
                <div className="flex items-center gap-2">
                  {/* Avatar — smaller */}
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      fontSize: '0.75rem',
                      background: player.isComputer
                        ? getCPUGradient(player.id || player.name)
                        : `linear-gradient(135deg, ${PLAYER_COLORS[idx % PLAYER_COLORS.length]}80, ${PLAYER_COLORS[idx % PLAYER_COLORS.length]})`,
                      border: `2px solid ${player.isComputer ? 'rgba(255,255,255,0.2)' : PLAYER_COLORS[idx % PLAYER_COLORS.length]}`,
                      boxShadow: `0 0 8px ${player.isComputer ? 'rgba(155,89,182,0.4)' : PLAYER_COLORS[idx % PLAYER_COLORS.length] + '40'}`,
                      flexShrink: 0,
                      fontFamily: '"Cinzel", serif',
                      paddingBottom: player.isComputer ? '2px' : '0', // Emoji slight alignment
                    }}
                  >
                    {player.isComputer ? getCPUAvatar(player.id || player.name) : player.name[0].toUpperCase()}
                  </div>

                  <div>
                    <div style={{ fontWeight: '600', color: '#f5ead2', fontSize: '0.88rem' }}>
                      {player.name}
                      {player.id === playerId && (
                        <span style={{ color: '#d4a843', fontSize: '0.7rem', marginLeft: '6px', fontFamily: '"Cinzel",serif' }}>(You)</span>
                      )}
                      {player.id === room.hostId && (
                        <span style={{ fontSize: '0.65rem', marginLeft: '6px', color: '#f2cc70' }}>👑 Host</span>
                      )}
                    </div>
                    {player.isComputer && (
                      <div style={{ fontSize: '0.65rem', color: 'rgba(212,168,67,0.5)' }}>AI Player</div>
                    )}
                  </div>
                </div>

                {isHost && player.isComputer && (
                  <button
                    onClick={() => onRemoveCPU(player.id)}
                    style={{ color: '#e74c3c', fontSize: '0.7rem', background: 'none', border: 'none', cursor: 'pointer' }}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Add CPU button */}
          {isHost && room?.players?.length < room?.settings?.maxPlayers && (
            <button
              onClick={onAddCPU}
              className="w-full mt-1 py-2 transition-all"
              style={{
                border: '2px dashed rgba(212,168,67,0.3)',
                borderRadius: '6px',
                color: 'rgba(212,168,67,0.6)',
                background: 'rgba(13,31,20,0.3)',
                cursor: 'pointer',
                fontFamily: '"Noto Serif SC", serif',
                fontSize: '0.85rem',
              }}
              onMouseEnter={e => { e.target.style.borderColor = 'rgba(212,168,67,0.6)'; e.target.style.color = '#d4a843'; }}
              onMouseLeave={e => { e.target.style.borderColor = 'rgba(212,168,67,0.3)'; e.target.style.color = 'rgba(212,168,67,0.6)'; }}
            >
              🏮 Add AI Player
            </button>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex gap-4 w-full">
          <button onClick={onLeaveRoom} className="btn-secondary">
            Leave Room
          </button>
          {isHost && (
            <button
              onClick={onStartGame}
              disabled={!canStart || !preloadComplete}
              className={`btn-primary flex-1 ${!preloadComplete ? 'opacity-80' : ''}`}
              style={{ minWidth: '140px' }}
            >
              {!preloadComplete 
                ? `Loading Assets... ${preloadProgress}%` 
                : canStart 
                  ? '▶ Start Game' 
                  : 'Need 2+ Players'}
            </button>
          )}
        </div>

        {!isHost && (
          <div style={{ color: 'rgba(245,234,210,0.4)', marginTop: '12px', fontSize: '0.82rem', fontFamily: '"Noto Serif SC",serif', textAlign: 'center' }}>
            Waiting for host to start the game...
            {!preloadComplete && (
              <div className="mt-1 opacity-70 text-xs">
                (Loading your assets: {preloadProgress}%)
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Lobby;
